import { Controller } from '@hotwired/stimulus';
export default class extends Controller {
  static targets = [
    "form",
    "formType",
    "hourlyRate",
    "hoursSpent",
    "billableAmount",
    "billable",
    "onlyInternal",
    "onlyClient",
    "onlyBillable",
    "service",
    "account",
    "tags",
    "previouslySelectedService",
    "metered",
    "meteredInfo",
    "billedAt",
    "serviceName",
    "task",
    "taskFrame"
  ];

  static values = { persisted: Boolean };

  connect() {
    this.initializing = true;
    this.accountChanged(true)
    this.changeForm()
    this.totalBillableAmount()
  }

  hourlyRateChanged() {
    if(this.billableTarget.checked) this.totalBillableAmount()
  }

  hoursSpentChanged() {
    if(this.billableTarget.checked) this.totalBillableAmount();
  }

  totalBillableAmount() {
    this.billableAmountTarget.value = (this.hourlyRateTarget.value * this.hoursSpentTarget.value).toFixed(2)
  }

  serviceChanged() {
    if(this.isSelectedServiceBillable()) {
      this.billableTarget.checked = true
      this.hourlyRateTarget.value = (this.service().dataset.billableRate / 100).toFixed(2)
      this.totalBillableAmount();
    } else {
      this.billableTarget.checked = false
    }

    this.setServiceName();
    this.toggleMetered();
    this.toggleBillableTargets();
    if (!this.initializing) {
      this.reloadTaskFrame({ clearTask: true });
    }
  }

  setServiceName() {
    const service = this.service()
    if (!service) {return;}
    const serviceName = this.serviceNameTarget
    if(service.value === "") {
      serviceName.value = ""
    } else {
      serviceName.value = service.dataset.name
    }
  }

  isSelectedServiceBillable() {
    const service = this.service()
    if(service.value === "") { return false }
    return service.getAttribute("data-service-billable") == "true";
  }

  overrideBillable() {
    if (this.service() && this.isSelectedServiceBillable() && !this.billableTarget.checked) {
      return confirm("This service is marked as billable. Are you sure you want to proceed?");
    } else { return true }
  }

  service() {
    const selectElement = this.serviceTarget;
    const index = selectElement.selectedIndex;
    return selectElement.options[index];
  }

  billableChanged() {
    if(this.overrideBillable()) {
      this.toggleBillableTargets()
      this.totalBillableAmount();
    }
    this.toggleMetered();
    this.applyMeteredRate();
  }

  toggleMetered() {
    this.meteredTarget.value = this.service() != undefined && this.service().dataset.metered === "true"
    this.syncMetered()
  }

  syncMetered() {
    this.toggleHourlyRate();
    this.setVisible(this.meteredInfoTargets, this.meteredTarget.value === 'true')
  }

  applyMeteredRate() {
    const rate = Number(this.service()?.dataset.billableRate)
    if(!this.billableTarget.checked || this.meteredTarget.value !== 'true' || !Number.isFinite(rate)) return

    this.hourlyRateTarget.value = (rate / 100).toFixed(2)
    this.totalBillableAmount()
  }

  toggleHourlyRate() {
    if(this.meteredTarget.value === 'true') {
      this.hourlyRateTarget.readOnly = true
      this.hourlyRateTarget.classList.add('bg-light')
      this.hourlyRateTarget.style.cursor = 'default'
    } else {
      this.hourlyRateTarget.readOnly = false
      this.hourlyRateTarget.classList.remove('bg-light')
      this.hourlyRateTarget.style.cursor = 'text'
    }
  }

  toggleBillableTargets() {
    this.setVisible(this.onlyBillableTargets, this.billableTarget.checked);
  }

  typeChanged() {
    this.serviceNameTarget.value = null
    this.changeForm()
  }

  changeForm() {
    if (this.formTypeTarget.value == "internal") {
      this.morphForm(this.onlyClientTargets, this.onlyInternalTargets);
      this.accountTarget.disabled = true
      this.serviceTarget.disabled = true
      this.billableTarget.disabled = true
      this.hourlyRateTarget.disabled = true
      this.tagsTarget.disabled = true
      if (this.hasTaskTarget) { this.taskTarget.value = "" }
    } else {
      this.morphForm(this.onlyInternalTargets, this.onlyClientTargets);
      if(!this.hasBilledAtTarget) { this.accountTarget.disabled = false }
      if(!this.hasBilledAtTarget) { this.serviceTarget.disabled = false;}
      if(!this.hasBilledAtTarget) { this.billableTarget.disabled = false }
      if(!this.hasBilledAtTarget) {
        this.hourlyRateTarget.disabled = false;
        this.toggleHourlyRate()
      }
      if(!this.hasBilledAtTarget) { this.tagsTarget.disabled = false }
      if(this.hasServiceNameTarget) { this.setServiceName() }
      this.toggleBillableTargets();
    }
  }

  accountChanged(arg) {
    const initializing = arg === true;
    const restoring = initializing && this.persistedValue;
    const accountId = this.accountTarget.value;
    if (accountId) {
      const savedService = { id: this.previouslySelectedServiceTarget.value, name: this.serviceNameTarget.value }
      this.serviceNameTarget.value = null
      const serviceSelect = this.serviceTarget;
      serviceSelect.innerHTML = ""
      serviceSelect.append(new Option("Loading...", ""));
      serviceSelect.disabled = true
      fetch(`/admin/timesheets/${accountId}/billable_services.json`)
        .then(response => response.json())
        .then(data => {
          if(data.length > 0) {
            serviceSelect.innerHTML = ""
            serviceSelect.append(new Option("Select a service...", ""));
            data.forEach(service => {
              let option = new Option(service.name, service.external_item_price_id);
              option.dataset.serviceBillable = service.billable;
              option.dataset.billableRate = service.unit_price;
              option.dataset.metered = service.metered;
              option.dataset.name = service.name;
              serviceSelect.append(option);

            });
            if(!restoring && this.previouslySelectedServiceTarget.value !== "") {
              serviceSelect.value = this.previouslySelectedServiceTarget.value;
              this.previouslySelectedServiceTarget.value = ""
              this.serviceChanged() // Update billable, hourly rate, metered, etc.
            }
          } else {
            serviceSelect.innerHTML = ""
            serviceSelect.append(new Option("No services found", ""));
          }
          if(restoring) { this.restoreService(savedService) }
          this.initializing = false;
        });
        if(!this.hasBilledAtTarget) { serviceSelect.disabled = false }
    }
    if (!initializing) { this.reloadTaskFrame(); }
  }

  restoreService({ id, name }) {
    this.previouslySelectedServiceTarget.value = ""
    const serviceSelect = this.serviceTarget
    serviceSelect.value = id
    if(serviceSelect.value !== id) {
      const option = new Option(name, id, true, true)
      option.dataset.name = name
      option.dataset.metered = this.meteredTarget.value
      option.dataset.serviceBillable = this.meteredTarget.value
      serviceSelect.append(option)
    }
    this.serviceNameTarget.value = name
    this.syncMetered()
    this.toggleBillableTargets()
    this.totalBillableAmount()
  }

  reloadTaskFrame({ clearTask = false } = {}) {
    if (!this.hasTaskFrameTarget) return;
    const frame = this.taskFrameTarget;
    const accountId = this.accountTarget.value;
    if (!accountId) {
      frame.removeAttribute("src");
      frame.innerHTML = '<div class="form-control text-muted small bg-light">Select an account to select a task</div>';
      return;
    }
    // Force a re-fetch: drop lazy once we've started interacting, clear src, then set it.
    frame.removeAttribute("loading");
    frame.removeAttribute("src");

    const serviceId = this.serviceTarget.value;
    const taskId = !clearTask && this.hasTaskTarget ? this.taskTarget.value : null;
    const params = new URLSearchParams();
    if (serviceId) params.set('service_id', serviceId);
    if (taskId) params.set('task_id', taskId);
    const query = params.toString();
    frame.src = `/admin/timesheets/${accountId}/tasks${query ? `?${query}` : ''}`;
  }

  protected;

  morphForm(hideList, showList) {
    this.setVisible(hideList, false);
    this.setVisible(showList, true);
  }

  setVisible(elements, visible) {
    elements.forEach((element) => { element.style.display = visible ? '' : 'none' });
  }
}
